import { track } from '@amplitude/marketing-analytics-browser'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import Tooltip from 'react-simple-tooltip'
import styled from 'styled-components'

import { fetchCalendar } from 'api'
import Calendar, { type CalendarProps } from 'Components/Calendar'
import CalendarModal, { type SelectedTile } from 'Components/CalendarModal'
import Modal from 'Components/Common/Modal'
import PoweredBySked from 'Components/Common/PoweredBySked'
import type { TabViewValue } from 'Components/Navbar'
import {
  ExclamationMarkRoundedIcon,
  TileApprovedIcon,
  TileRejectedIcon,
  TilePendingIcon,
} from 'Icons'
import { normalizeCalendarEvents } from 'normalizer'
import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'
const ModalStyled = styled(Modal)`
  width: 900px;
`

const PoweredBySkedStyled = styled(PoweredBySked)`
  display: flex;
  justify-content: center;
  margin: 60px auto 30px;
`

const Legend = styled.div`
  > div {
    display: flex;
    align-items: center;
    top: 110px;
    left: 150px;
    font-size: 14px;
    font-weight: 700;
    position: absolute;
    z-index: 3;
    cursor: default;

    svg {
      width: 24px;
      height: 24px;
      margin-right: 6px;
    }
  }

  [data-testid='calendar-legend-tooltip'] {
    > div {
      left: 13px;

      > div {
        // content area of tooltip
        width: 660px;
        font-weight: 500;
        margin-left: 46%;
        background: #333333;
        border-color: #333333;
      }

      > div > div {
        // caret of tooltip
        background: #333333;
      }

      li {
        list-style: none;
        display: flex;
        align-items: center;
        &:not(:last-child) {
          margin-bottom: 8px;
        }

        &:first-child {
          margin-top: 16px;
        }

        > svg {
          width: 22px;
          height: 22px;
          margin-right: 11px;
        }
      }
    }
  }
`

interface CalendarPageProps {
  setTabView: (view: TabViewValue) => void
  isReadOnly: boolean
}

const CalendarPage: React.FC<CalendarPageProps> = ({
  setTabView,
  isReadOnly,
}) => {
  const reviewKey = getReviewKeyFromURL(window.location.href)

  const [currentSelectedDate, setCurrentSelectedDate] = useState<Date>(
    import.meta.env.VITE_IS_PLAYWRIGHT === 'true'
      ? new Date('12/01/2022') // set a specified date for integration test
      : new Date()
  )

  const [nextTileIndex, setNextTileIndex] = useState<number>(0)

  const [calendarEvents, setCalendarEvents] = useState<
    CalendarProps['events'] | []
  >([])
  const [selectedTile, setSelectedTile] = useState<SelectedTile | undefined>(
    undefined
  )

  const fetchCalendarAsync = async (
    reviewKey: string,
    currentSelectedDate: Date
  ) => {
    const calendarResponse = await fetchCalendar(reviewKey, currentSelectedDate)
    setCalendarEvents(normalizeCalendarEvents(calendarResponse))
  }

  useEffect(() => {
    fetchCalendarAsync(reviewKey, currentSelectedDate)
  }, [])

  const handleTileClick = (selectedTile: SelectedTile) => {
    setSelectedTile(selectedTile)

    // for navigation purpose
    setNextTileIndex(
      calendarEvents.findIndex((event) => event.id === selectedTile.id)
    )

    track('ext-cal-post-click')
  }

  const handleReviewPost = () => {
    track('ext-cal-post-review')

    setTabView('approval')

    if (selectedTile?.id) {
      const postId = selectedTile.id

      setTimeout(() => {
        const toBeSelectedPostElem = document.querySelector(
          `[data-postid="${postId}"`
        ) as HTMLDivElement

        toBeSelectedPostElem?.click()

        toBeSelectedPostElem?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }, 300)
    }
  }

  const handleSelectedDateChange = (changedDate: Date) => {
    setCurrentSelectedDate(changedDate)
    fetchCalendarAsync(reviewKey, changedDate)
  }

  const handleNavigationArrowClick =
    (direction: 'left' | 'right', currentId: string) => () => {
      let nextTileIndex = calendarEvents.findIndex(
        (event) => event.id === currentId
      )

      direction === 'right' ? nextTileIndex++ : nextTileIndex--
      setNextTileIndex(nextTileIndex)

      const {
        id,
        start,
        tileData: { time, platform, status },
      } = calendarEvents.find((_e, index) => index === nextTileIndex)!

      if (id) {
        setSelectedTile({
          id: id.toString(),
          date: format(new Date(start), 'MMM d yyyy'),
          time,
          platform,
          status,
        })
      }
    }

  const tooltipOptions = {
    radius: 5,
    placement: 'bottom',

    content: (
      <div>
        In the ‘Calendar View’ you’ll be able to see upcoming posts. Click on
        the posts to see a full preview. Posts have the following states:
        <li>
          <TileApprovedIcon />
          Approved posts.
        </li>
        <li>
          <TileRejectedIcon />
          Posts you’ve requested changes for.
        </li>
        <li>
          <TilePendingIcon />
          Posts that require your review.
        </li>
      </div>
    ),
  }

  return (
    <div>
      {selectedTile && (
        <ModalStyled
          open={!!selectedTile}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTile(undefined)
            }
          }}
        >
          <CalendarModal
            reviewKey={reviewKey}
            selectedTile={selectedTile}
            reviewPostClick={handleReviewPost}
            isReadOnly={isReadOnly}
            navigationArrowClick={handleNavigationArrowClick}
            hideArrowLeft={nextTileIndex === 0}
            hideArrowRight={nextTileIndex === calendarEvents.length - 1}
          />
        </ModalStyled>
      )}

      <Legend>
        {isReadOnly ? (
          <div>Upcoming Posts</div>
        ) : (
          <Tooltip data-testid="calendar-legend-tooltip" {...tooltipOptions}>
            <ExclamationMarkRoundedIcon />
            Upcoming Posts{' '}
          </Tooltip>
        )}
      </Legend>

      <Calendar
        events={calendarEvents}
        tileClick={handleTileClick}
        defaultSelectedDate={currentSelectedDate}
        onSelectedDateChange={handleSelectedDateChange}
        isReadOnly={isReadOnly}
      />
      <PoweredBySkedStyled />
    </div>
  )
}

export default CalendarPage
